import CameraIcon from '@mdi/svg/svg/camera-outline.svg?react';
import TransactionIcon from '@mdi/svg/svg/currency-usd.svg?react';
import LinkIcon from '@mdi/svg/svg/link-variant.svg?react';
import { type VideoLinkItem as VideoLinkItemType } from '@solink/vd-data-layer/api-clients/platform/platform';
import { Badge } from '@solinkcorp/solink-ui/components/Badge';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@solinkcorp/solink-ui/components/Dialog';
import moment from 'moment';
import { type Dispatch, type SetStateAction, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import TimeFormatter from 'utils/TimeFormatter';
import { type FieldRendererProps } from './base';

export function VideoLinkItemsV2(
  props: Pick<FieldRendererProps, 'fieldValue' | 'event' | 'camerasDictionary' | 'onEventRowClick'>,
) {
  const { fieldValue, onEventRowClick, event, camerasDictionary } = props;
  const videoLinkItems = fieldValue as VideoLinkItemType[];
  const timeFormat = TimeFormatter.getString({ seconds: true });

  return videoLinkItems.map((item, index) => (
    <VideoLinkItem
      key={index}
      item={item}
      event={event}
      camerasDictionary={camerasDictionary}
      onEventRowClick={onEventRowClick}
      timeFormat={timeFormat}
    />
  ));
}

interface VideoLinkItemProps extends Pick<
  FieldRendererProps,
  'event' | 'camerasDictionary' | 'onEventRowClick'
> {
  item: VideoLinkItemType;
  timeFormat: string;
}

function VideoLinkItem(props: VideoLinkItemProps) {
  const { item, event, camerasDictionary, onEventRowClick, timeFormat } = props;
  const locale = useLocale();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { eventId: sourceEventId, eventType: sourceEventType } = item.metadata ?? {};
  const itemCameraId = item.cameraId;
  const chCameraId = Object.keys(camerasDictionary).find(
    (c) => camerasDictionary[c].cameraId === itemCameraId,
  );
  const cameraName = camerasDictionary[chCameraId ?? '']?.name ?? itemCameraId;
  const buttonClass =
    'inline-flex items-center gap-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-400 px-1.5 py-0.5 text-[10px] text-gray-900 dark:text-gray-1000 hover:bg-gray-100 dark:hover:bg-gray-500 [&_svg]:fill-gray-900 dark:[&_svg]:fill-gray-1000';
  return (
    <div className="tw flex gap-x-4">
      <div className="flex flex-col items-center">
        <div className="h-2 w-2 flex-shrink-0 self-center rounded-full bg-gray-600 dark:bg-gray-200" />
        <div className="h-full w-px justify-self-center bg-gray-300 dark:bg-gray-500" />
      </div>

      <div className="flex flex-1 flex-col gap-1 pb-2">
        <div className="-mt-[5px] flex w-full justify-between">
          <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-gray-50">
            <div className="flex items-center gap-1" data-testid="video-link-item-title">
              {item.title}{' '}
            </div>
          </div>
          <div className="self-center whitespace-nowrap text-[11px] font-medium text-gray-600 dark:text-gray-300">
            {moment(item.timestamp).format(timeFormat)}
          </div>
        </div>

        {item.metadata?.summary && (
          <div className="text-xs font-medium text-gray-600 dark:text-gray-100">
            {item.metadata.summary}
          </div>
        )}

        {!!item.metadata?.description && (
          <div className="text-xs font-normal text-gray-600 dark:text-gray-300">
            {item.metadata.description}
          </div>
        )}

        {!!item.metadata?.additionalDetails && (
          <div className="pt-0.5">
            <button
              className="cursor-pointer text-[11px] text-turquoise-600 dark:text-turquoise-500"
              onClick={() => setIsDialogOpen(true)}
            >
              {locale.videoLinkItems.viewDetails}
            </button>
          </div>
        )}

        <AgentOutputsList
          outputs={event?.details?.agentOutputs as Record<string, unknown> | undefined}
        />

        <VideoLinkItemDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} item={item} />

        {!!itemCameraId && (
          <div className="pt-0.5">
            <button
              className={buttonClass}
              onClick={() => onEventRowClick(event, itemCameraId, item.timestamp)}
            >
              <CameraIcon height={12} width={12} />
              {cameraName}
            </button>
          </div>
        )}

        {!!sourceEventId && (
          <div className="pt-0.5">
            <a
              className={buttonClass}
              href={`/events?eventId=${sourceEventId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {/* Missing eventType is legacy LP: those links only stored eventId. */}
              {sourceEventType === 'transaction' || sourceEventType == null ? (
                <>
                  <TransactionIcon height={12} width={12} />
                  {locale.dashboard.transaction}
                </>
              ) : (
                <>
                  <LinkIcon height={12} width={12} />
                  {locale.videoLinkItems.viewSourceEvent}
                </>
              )}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function formatOutputValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value == null) return '—';
  return String(value);
}

function AgentOutputsList({ outputs }: { outputs?: Record<string, unknown> }) {
  if (!outputs || Object.keys(outputs).length === 0) return null;

  return (
    <div className="mt-2 border-t border-gray-200 pt-2.5 dark:border-gray-600">
      <div className="flex flex-col gap-1">
        {Object.entries(outputs).map(([key, value]) => (
          <div key={key} className="text-xs">
            <span className="text-gray-900 dark:text-gray-100">{key}: </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatOutputValue(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface VideoLinkItemDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  item: VideoLinkItemType;
}

function VideoLinkItemDialog(props: VideoLinkItemDialogProps) {
  const { open, onOpenChange, item } = props;
  const locale = useLocale();
  const dateFormat = item.timestamp
    ? TimeFormatter.getDateTime({ seconds: true }, new Date(item.timestamp))
    : null;

  const fields = [
    {
      label: locale.videoLinkItems.date,
      content: <div className="flex items-center gap-2 text-xs text-gray-600">{dateFormat}</div>,
    },
    {
      label: locale.videoLinkItems.recipients,
      content: (
        <div className="flex flex-wrap items-center gap-2">
          {item.metadata?.additionalDetails?.notificationRecipients?.map((r, index) => (
            <Badge variant="outline" key={index}>
              {r.email ?? r.phoneNumber}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      label: locale.videoLinkItems.subject,
      content: (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          {item.metadata?.additionalDetails?.subject}
        </div>
      ),
    },
    {
      label: locale.videoLinkItems.body,
      content: (
        <div className="flex flex-col gap-1 rounded-md border border-gray-100 bg-[#FAFAFA] p-3 text-xs text-gray-600">
          <div className="font-bold">{item.metadata?.additionalDetails?.title}</div>
          <div>{item.metadata?.additionalDetails?.description}</div>
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{item.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {fields.map((field) => (
            <div key={field.label}>
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-900">
                {field.label}
              </span>
              {field.content}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {locale.general.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
